export interface DashboardSchedule {
  id: string;
  name: string;
  description: string;
  cronExpression: string;
  enabled: boolean;
  running: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastFinishedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  lastMessage: string | null;
  lastRunTrigger: "manual" | "cron" | null;
  runCount: number;
  successCount: number;
  failureCount: number;
  updatedAt: string;
}

const fetchOptions: RequestInit = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

async function readApiError(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const payload = (await response.json()) as {
      error?: string;
      detail?: string;
      message?: string;
    };
    return payload.error ?? payload.detail ?? payload.message ?? fallback;
  } catch {
    return fallback;
  }
}

export const schedulesService = {
  async listSchedules(): Promise<DashboardSchedule[]> {
    const response = await fetch("/api/internal/admin/schedules", fetchOptions);
    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to fetch schedules"));
    }

    const payload = (await response.json()) as {
      schedules?: DashboardSchedule[];
    };
    return payload.schedules ?? [];
  },

  async setScheduleEnabled(
    scheduleId: string,
    enabled: boolean,
  ): Promise<DashboardSchedule> {
    const response = await fetch(
      `/api/internal/admin/schedules/${encodeURIComponent(scheduleId)}`,
      {
        ...fetchOptions,
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      },
    );
    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to update schedule"));
    }

    const payload = (await response.json()) as {
      schedule: DashboardSchedule;
    };
    return payload.schedule;
  },

  async runScheduleNow(scheduleId: string): Promise<DashboardSchedule> {
    const response = await fetch(
      `/api/internal/admin/schedules/${encodeURIComponent(scheduleId)}/run`,
      {
        ...fetchOptions,
        method: "POST",
      },
    );
    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to run schedule"));
    }

    const payload = (await response.json()) as {
      schedule: DashboardSchedule;
    };
    return payload.schedule;
  },
};
